import { useEffect, useCallback, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserPreferences } from './useUserPreferences';
import { useSystemSetting } from './useSystemSettings';
import { usePermissions } from './usePermissions';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

const WARNING_TIME = 60 * 1000; // 60 segundos de warning antes do logout
const LAST_ACTIVITY_KEY = 'arete:last_activity';

export const useSessionTimeout = () => {
  const { signOut, user } = useAuth();
  const { data: preferences } = useUserPreferences();
  // Only query system settings when user is authenticated to avoid 406 errors
  const { data: globalSetting } = useSystemSetting("global_session_timeout_minutes", { enabled: !!user });
  const { isAdmin, isSupervisor } = usePermissions();
  const { toast } = useToast();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const [showWarning, setShowWarning] = useState(false);

  // Função para obter o timeout configurado
  const getSessionTimeout = useCallback(() => {
    const userTimeout = preferences?.session_timeout_minutes;
    const globalTimeout = globalSetting?.value ? parseInt(globalSetting.value) : 30;
    
    // Priorizar preferência do usuário, senão usar global
    let timeoutMinutes = userTimeout || globalTimeout;
    
    // Se o usuário não é admin/supervisor, limitar baseado no papel
    if (!isAdmin() && !isSupervisor()) {
      // Usuários regulares: máximo 1 hora
      timeoutMinutes = Math.min(timeoutMinutes, 60);
    } else {
      // Admin/Supervisor: máximo 2 horas
      timeoutMinutes = Math.min(timeoutMinutes, 120);
    }
    
    // Converter para ms
    return timeoutMinutes * 60 * 1000;
  }, [preferences, globalSetting, isAdmin, isSupervisor]);

  // Função para fazer logout por timeout
  const handleTimeout = useCallback(async () => {
    setShowWarning(false);
    try { localStorage.removeItem(LAST_ACTIVITY_KEY); } catch {}
    toast({
      title: "Sessão expirada",
      description: "Sua sessão expirou por inatividade.",
      variant: "destructive",
    });
    await signOut();
  }, [signOut, toast]);

  // Função para mostrar o warning
  const handleShowWarning = useCallback(() => {
    setShowWarning(true);
  }, []);

  // Persistir última atividade no localStorage
  const persistActivity = useCallback((ts: number) => {
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(ts)); } catch {}
  }, []);

  // Função para resetar o timer (com tempo restante opcional)
  const resetTimeout = useCallback((remainingMs?: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    if (!user) return;

    const timeout = getSessionTimeout();
    const effective = typeof remainingMs === 'number' ? Math.max(remainingMs, 0) : timeout;

    // Se está iniciando timer "fresco", marca atividade agora
    if (typeof remainingMs !== 'number') {
      const now = Date.now();
      lastActivityRef.current = now;
      persistActivity(now);
    }
    setShowWarning(false);

    const warningTime = Math.max(effective - WARNING_TIME, 0);
    warningRef.current = setTimeout(handleShowWarning, warningTime);
    timeoutRef.current = setTimeout(handleTimeout, effective);
  }, [user, getSessionTimeout, handleTimeout, handleShowWarning, persistActivity]);

  // Função para prolongar sessão (usuário clicou para continuar)
  const extendSession = useCallback(async () => {
    setShowWarning(false);
    // Renova o token JWT no Supabase para não cair por refresh token expirado
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        logger.error('Falha ao renovar sessão:', error);
        toast({
          title: 'Sessão não pôde ser renovada',
          description: 'Faça login novamente para continuar.',
          variant: 'destructive',
        });
        await handleTimeout();
        return;
      }
    } catch (e) {
      logger.error('Erro inesperado ao renovar sessão:', e);
    }
    resetTimeout();
  }, [resetTimeout, handleTimeout, toast]);

  // Função para registrar atividade
  const recordActivity = useCallback(() => {
    if (showWarning) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    if (timeSinceLastActivity > 60 * 1000) {
      resetTimeout();
    }
  }, [resetTimeout, showWarning]);

  // Verifica atividade armazenada (ao montar e ao voltar foco)
  const checkStoredActivity = useCallback(() => {
    if (!user) return;
    const timeout = getSessionTimeout();
    let stored: number | null = null;
    try {
      const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
      stored = raw ? parseInt(raw, 10) : null;
    } catch {}

    const now = Date.now();
    if (stored && now - stored > timeout) {
      handleTimeout();
      return;
    }

    const remaining = stored ? timeout - (now - stored) : timeout;
    lastActivityRef.current = stored ?? now;
    if (!stored) persistActivity(now);
    resetTimeout(remaining);
  }, [user, getSessionTimeout, handleTimeout, resetTimeout, persistActivity]);

  // Configurar listeners de atividade
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, recordActivity, { passive: true });
    });

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkStoredActivity();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Inicializar com base na atividade persistida
    checkStoredActivity();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, recordActivity);
      });
      document.removeEventListener('visibilitychange', onVisibility);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [user, recordActivity, checkStoredActivity]);


  return {
    resetTimeout,
    recordActivity,
    extendSession,
    showWarning,
    handleTimeout,
    getSessionTimeout: () => getSessionTimeout() / (60 * 1000), // retorna em minutos
  };
};